// @givmo/board — reading the annual conflict-of-interest disclosures (CBG-YR2-WEB1/WEB2; the
// backend's `agent_docs/contracts/board-coi-cycle.md`).
//
// ══ WHAT THESE READS ARE, AND WHAT THEY ARE NOT ═════════════════════════════════════════════════
//
// The year itself is the backend's WORKING STATE (who was asked, who submitted and when, what was sent
// and when); what the year produces are RECORDS — each person's signed answers a `disclosure` record,
// the closed year a `disclosure_cycle` record. Every read here is either the working state's facts or a
// record's own words, and the screens render them as they come: nothing is computed from them that
// the backend did not say, and nothing here concludes anything about anyone's interests. The one count
// on any screen is the backend's count of SUBMISSIONS (`submitted_count` of `people_count`); there is
// no count of conflicts, at any spelling.
//
// Tolerant, as every Board decoder is: an answer missing a field, a list that is not an array, or a
// value this build doesn't know never throws. A malformed row is dropped; an unknown word is shown
// humanized rather than hidden.

import type {
  BoardCoiAnswerOut,
  BoardCoiCycleOut,
  BoardCoiCyclePersonOut,
  BoardCoiCycleSummaryOut,
  BoardCoiDirectorCycleOut,
  BoardCoiEntryFieldsOut,
  BoardCoiEntryOut,
  BoardCoiPersonQuestionnaireOut,
  BoardCoiQuestionAskedOut,
  BoardCoiQuestionChangeOut,
  BoardCoiQuestionOut,
  BoardCoiQuestionnaireForMeOut,
  BoardCoiQuestionnaireOut,
  BoardCoiQuestionnaireSummaryOut,
  BoardCoiResponseChangesOut,
  BoardCoiResponseOut,
  BoardConflictAnnualOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { joinWords } from '../roster/officer-roles';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { coiWords as w } from './coi-words';

// ── The shape of an answer ─────────────────────────────────────────────────────────────────────────

/** The four kinds of question Givmo's starting questionnaire asks (`board_coi_questionnaires.py`). */
export const KIND_ACKNOWLEDGMENT = 'acknowledgment';
export const KIND_ENTRIES = 'entries';
export const KIND_YES_NO = 'yes_no';
export const KIND_TEXT = 'text';

/** The two parts every answer belongs to besides interests. */
export const PART_POLICY = 'policy';
export const PART_OTHER = 'other';

/** An entry's fields in the order a line names them: what it is first, then the rest. */
export const ENTRY_FIELDS = [
  'name',
  'person',
  'whose',
  'what',
  'position',
  'relationship',
] as const;
export type EntryField = (typeof ENTRY_FIELDS)[number] | 'details';

/** An entry's fields, whichever read carried it (a recorded entry, a change's before or after). */
export type EntryLike = Partial<Record<EntryField, string | null>> & { key?: string | null };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function arrayOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter(isObject) as T[]) : [];
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** An entry as one line: what it names first, then the rest, joined by " · " (o6-answers). */
export function entryLead(entry: EntryLike): string {
  return text(entry.name) ?? text(entry.person) ?? '';
}

export function entryRest(entry: EntryLike): string[] {
  return ENTRY_FIELDS.filter((field) => field !== 'name' && field !== 'person')
    .map((field) => labelled(field, text(entry[field])))
    .filter((value): value is string => value !== null);
}

/** "Whose: Mine", "Position: Director": the two answers an entry labels, the register's way (D10). */
function labelled(field: EntryField, value: string | null): string | null {
  if (value === null) return null;
  if (field === 'whose') return w.whose(value);
  if (field === 'position') return w.position(value);
  return value;
}

/** "Yes" / "No" / the words / "Nothing added.", for any answer's value. */
export function answerWord(value: unknown, kind?: string | null): string {
  if (value === true || value === 'yes') return w.yes;
  if (value === false || value === 'no') return w.no;
  if (kind === KIND_TEXT || typeof value === 'string') return text(value) ?? w.nothingAdded;
  return '—';
}

// ── The questionnaire (Givmo's starting one, and a year's questions as someone is asked them) ──────

export function extractQuestionnaires(body: unknown): BoardCoiQuestionnaireSummaryOut[] {
  return extractList<BoardCoiQuestionnaireSummaryOut>(body, 'data').filter(isObject);
}

export function decodeQuestionnaire(body: unknown): BoardCoiQuestionnaireOut | null {
  if (!isObject(body) || typeof body.template_id !== 'string') return null;
  const questionnaire = body as unknown as BoardCoiQuestionnaireOut;
  return { ...questionnaire, questions: arrayOf<BoardCoiQuestionOut>(body.questions) };
}

/** Whether a question is asked of everyone, or of directors only (`I-5`, Q3). */
export function askedOfDirectorsOnly(question: { asked_of?: string | null }): boolean {
  return question.asked_of === 'directors';
}

/** The words a question's `{org}` and `{period}` become: the backend's `fill`, for the preview. */
export function fillQuestion(words: string, organization: string, period: string): string {
  return words.replaceAll('{org}', organization).replaceAll('{period}', period);
}

// ── Dates ───────────────────────────────────────────────────────────────────────────────────────

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A local `Date` for a calendar date (never moved by a zone) or an instant, or null. */
function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parts = DATE_ONLY.exec(iso);
  if (parts === null) {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
}

/** A day's month and number kept on one line wherever a sentence wraps (d1-year-card's rule). The
 *  record page's approval lines keep their day by it too (`records-decode.ts`, `writtenOutMoment`),
 *  and the minutes' names on the pages that show them (`namedMeetingDate`). */
export const oneLine = (words: string) => words.replace(/ /g, '\u00a0');

/** "Jan 29": a day inside the year's own story (the dashboard's dates; o3-dashboard). */
export function shortDay(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null
    ? '—'
    : oneLine(date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
}

/** "January 29": the person's own copy (d1-year-card, d2-fresh). */
export function longDay(iso: string | null | undefined): string {
  const date = parse(iso);
  return date === null
    ? '—'
    : oneLine(date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' }));
}

/** "Jan 14, 2027 at 8:05 PM": an instant in the reader's own zone (o6-answers, d5-signed). */
export function moment(iso: string | null | undefined): string {
  const date = parse(iso);
  if (date === null) return '—';
  const day = date.toLocaleDateString(undefined, { dateStyle: 'medium' });
  const time = date.toLocaleTimeString(undefined, { timeStyle: 'short' });
  return `${day} at ${time}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * "January 29, 2027" — the backend's own `long_date`, in English whatever the reader's locale: the
 * form the mails print their due date in, so the email preview shows the date as it is sent.
 */
export function mailDate(iso: string | null | undefined): string {
  const parts = DATE_ONLY.exec(iso ?? '');
  if (parts === null) return '';
  return `${MONTHS[Number(parts[2]) - 1] ?? ''} ${Number(parts[3])}, ${parts[1]}`;
}

/**
 * The period's words, by the backend's own rule (`period_label`): the year ("2026") when the period
 * is exactly one calendar year, else both days ("July 1, 2025 to June 30, 2026"). The start dialog
 * needs it before the year exists, to fill the questions it previews.
 */
export function periodLabel(start: string, end: string): string {
  const a = DATE_ONLY.exec(start);
  const b = DATE_ONLY.exec(end);
  if (a === null || b === null) return '';
  if (a[1] === b[1] && a[2] === '01' && a[3] === '01' && b[2] === '12' && b[3] === '31')
    return a[1] ?? '';
  return `${mailDate(start)} to ${mailDate(end)}`;
}

/** Today as a calendar date in the reader's zone (`YYYY-MM-DD`). */
export function todayIso(nowMs: number = Date.now()): string {
  const date = new Date(nowMs);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Whether a due date has passed, the reader's today against it (the reminder's "It was due"). */
export function isPastDue(dueOn: string | null | undefined, nowMs: number = Date.now()): boolean {
  return typeof dueOn === 'string' && DATE_ONLY.test(dueOn) && todayIso(nowMs) > dueOn;
}

// ── The year's list, and the year itself ─────────────────────────────────────────────────────────

export function extractCycles(body: unknown): BoardCoiCycleSummaryOut[] {
  return extractList<BoardCoiCycleSummaryOut>(body, 'data').filter(
    (row) => isObject(row) && typeof row.id === 'string' && typeof row.label === 'string',
  );
}

export function decodeCycle(body: unknown): BoardCoiCycleOut | null {
  if (!isObject(body) || typeof body.id !== 'string') return null;
  const cycle = body as unknown as BoardCoiCycleOut;
  return {
    ...cycle,
    people: arrayOf<BoardCoiCyclePersonOut>(body.people).filter(
      (row) => typeof row.board_member_id === 'string',
    ),
  };
}

/** The organization's open year, if any: the one cycle `open`. */
export function openCycleOf(
  cycles: readonly BoardCoiCycleSummaryOut[],
): BoardCoiCycleSummaryOut | null {
  return cycles.find((cycle) => cycle.status === 'open') ?? null;
}

/**
 * The year that takes the answer of someone joining the board today (CBG-YR6-OUTSIDE-WIDEN; backend
 * `board-coi-cycle.md` §22.2): the open year, else the organization's latest year once it has closed
 * — whoever joins after a close may be sent it — else none (no year yet, or none but withdrawn ones).
 * A withdrawn year is passed over, as the backend's `latest_cycle_for_org` passes it over.
 */
export function yearTakingAJoinerOf(
  cycles: readonly BoardCoiCycleSummaryOut[],
): BoardCoiCycleSummaryOut | null {
  const open = openCycleOf(cycles);
  if (open !== null) return open;
  const latest = [...cycles]
    .filter((row) => row.status !== 'cancelled')
    .sort((a, b) => ((a.opened_at ?? '') < (b.opened_at ?? '') ? 1 : -1))[0];
  return latest !== undefined && latest.status === 'closed' ? latest : null;
}

/**
 * The year before this one, by when each was opened: the latest cycle opened before it that wasn't
 * withdrawn. Its label heads "Changes from 2026"; null in a first year.
 */
export function previousCycleOf(
  cycles: readonly BoardCoiCycleSummaryOut[],
  cycle: { id: string; opened_at?: string | null },
): BoardCoiCycleSummaryOut | null {
  const opened = cycle.opened_at ?? '';
  const earlier = cycles
    .filter((row) => row.id !== cycle.id && row.status !== 'cancelled')
    .filter((row) => (row.opened_at ?? '') < opened)
    .sort((a, b) => ((a.opened_at ?? '') < (b.opened_at ?? '') ? 1 : -1));
  return earlier[0] ?? null;
}

/** The label the year after the latest one would carry: this calendar year, or the next free one. */
export function nextLabel(
  cycles: readonly BoardCoiCycleSummaryOut[],
  nowMs: number = Date.now(),
): string {
  const taken = new Set(cycles.filter((row) => row.status !== 'cancelled').map((row) => row.label));
  let year = new Date(nowMs).getFullYear();
  while (taken.has(String(year))) year += 1;
  return String(year);
}

/** A person's facts on the board, as the roster's capacity line says them ("Director · Chair"). */
export function capacityOf(person: {
  is_director?: boolean | null;
  title_labels?: readonly string[] | null;
}): string {
  const labels = Array.isArray(person.title_labels)
    ? person.title_labels.filter((label) => typeof label === 'string' && label.length > 0)
    : [];
  if (person.is_director === true)
    return labels.length > 0 ? roles.directorWithTitles(joinWords(labels)) : roles.directorAlone;
  return labels.length > 0 ? roles.titlesNotDirector(joinWords(labels)) : roles.notDirector;
}

export type StatusTone = 'plain' | 'act' | 'quiet';

/** One person's status on the year, as plain words (o3-dashboard). Colour only where the officer acts. */
export function statusOf(
  person: BoardCoiCyclePersonOut,
  yearOpen = true,
): {
  word: string;
  tone: StatusTone;
  notes: string[];
} {
  switch (person.status) {
    case 'submitted': {
      const notes: string[] = [];
      if (person.road === 'paper') notes.push(w.onPaper);
      if (person.corrected_at) notes.push(w.correctedOn(shortDay(person.corrected_at)));
      if (person.received_after_close) notes.push(w.receivedAfterClose);
      return { word: w.statusSubmitted(shortDay(person.submitted_at)), tone: 'plain', notes };
    }
    case 'not_sent':
      // A closed year sends nothing more, so quiet (CBG-YR2-WEB3). A joiner it never went to is
      // offered nothing (contract §5.3); someone on its list whose request never went out can still
      // answer on paper (§5.6; `offersPaperForm`).
      return {
        word: yearOpen ? w.statusNotSent : w.statusNotSentClosed,
        tone: yearOpen ? 'act' : 'quiet',
        notes: person.joined_after_open_on
          ? [w.joinedBoardOn(shortDay(person.joined_after_open_on))]
          : [],
      };
    case 'left':
      return {
        word: w.statusLeft(person.left_on ? shortDay(person.left_on) : null),
        tone: 'quiet',
        notes: [],
      };
    default: {
      const notes: string[] = [];
      if (!person.has_email) notes.push(w.noEmailOnRoster);
      else if (!person.joined_board) notes.push(w.hasntJoined);
      if (person.last_reminded_at) notes.push(w.remindedOn(shortDay(person.last_reminded_at)));
      return { word: w.statusNotSubmitted, tone: 'quiet', notes };
    }
  }
}

/** Who hasn't submitted, by name, among the people still serving (the close dialog's "Not yet"). */
export function outstandingNames(people: readonly BoardCoiCyclePersonOut[]): string[] {
  return people
    .filter((person) => person.status === 'not_submitted' || person.status === 'not_sent')
    .map((person) => person.display_name ?? '')
    .filter((name) => name.length > 0);
}

/** The people a reminder can go to now (the backend's `can_remind`). */
export function remindablePeople(
  people: readonly BoardCoiCyclePersonOut[],
): BoardCoiCyclePersonOut[] {
  return people.filter((person) => person.can_remind === true);
}

/**
 * Whether a first answer can be recorded for this person on paper now, and so their form printed:
 * someone still to answer; someone asked who has since left the board and hasn't answered, whose
 * form the paper road still takes (the contract's §5.2, v0's rule: they disclosed while on the
 * board); a joiner the year hasn't been sent to, while it is open (§5.3, C4); or someone on the
 * year's list whose request never went out — held back by the environment's recipient guard, or
 * a send that failed — whom a closed year still takes a late answer from (§5.6; review-134's F1).
 * The person read says it exactly before the form opens (`accepts_answers`); this only decides
 * whether the row offers it.
 */
export function offersPaperForm(person: BoardCoiCyclePersonOut, yearOpen: boolean): boolean {
  if (person.status === 'not_submitted') return true;
  if (person.status === 'left') return person.is_recipient && !person.response_record_id;
  // CBG-YR4-COI-JOINER: a closed year's joiner's paper form creates their row, as an open year's does.
  return (
    person.status === 'not_sent' &&
    (yearOpen || person.is_recipient === true || person.joined_after_close === true)
  );
}

// ── One person's answers ─────────────────────────────────────────────────────────────────────────

export function decodeResponse(body: unknown): BoardCoiResponseOut | null {
  if (!isObject(body) || typeof body.record_id !== 'string') return null;
  const response = body as unknown as BoardCoiResponseOut;
  return {
    ...response,
    answers: arrayOf<BoardCoiAnswerOut>(body.answers).map((answer) => ({
      ...answer,
      entries: arrayOf<BoardCoiEntryOut>(answer.entries),
      points_as_asked: Array.isArray(answer.points_as_asked) ? answer.points_as_asked : [],
    })),
  };
}

export function decodeChanges(body: unknown): BoardCoiResponseChangesOut | null {
  if (!isObject(body) || !isObject(body.current)) return null;
  const changes = body as unknown as BoardCoiResponseChangesOut;
  return {
    ...changes,
    questions: arrayOf<BoardCoiQuestionChangeOut>(body.questions).map((question) => ({
      ...question,
      entries: arrayOf(question.entries),
    })),
  };
}

/** The response's own record, read back: the clean copy's sections (o6-answers). */
export function answersByPart(response: Pick<BoardCoiResponseOut, 'answers'>) {
  const statement = response.answers.find((answer) => answer.part === PART_POLICY) ?? null;
  const interests = response.answers.filter(
    (answer) => answer.part !== PART_POLICY && answer.part !== PART_OTHER,
  );
  const other = response.answers.filter((answer) => answer.part === PART_OTHER);
  return { statement, interests, other };
}

/** Field values of a change's before or after side, as a line reads them. */
export function changeFields(fields: BoardCoiEntryFieldsOut | null | undefined): EntryLike {
  return isObject(fields) ? (fields as EntryLike) : {};
}

// ── A person's questionnaire (the officer's paper road, and the person's own) ─────────────────────

function decodeAsked<T extends BoardCoiPersonQuestionnaireOut>(body: unknown): T | null {
  if (!isObject(body) || !isObject(body.cycle)) return null;
  const read = body as unknown as T;
  return {
    ...read,
    questions: arrayOf<BoardCoiQuestionAskedOut>(body.questions).map((question) => ({
      ...question,
      points_as_asked: Array.isArray(question.points_as_asked) ? question.points_as_asked : [],
      fields: arrayOf(question.fields),
      help: Array.isArray(question.help)
        ? question.help.filter((line) => typeof line === 'string')
        : [],
    })),
    previous: isObject(body.previous)
      ? {
          ...(body.previous as unknown as NonNullable<T['previous']>),
          answers: arrayOf<NonNullable<T['previous']>['answers'][number]>(
            body.previous.answers,
          ).map((answer) => ({ ...answer, entries: arrayOf<BoardCoiEntryOut>(answer.entries) })),
        }
      : null,
    disclosures_since: arrayOf(body.disclosures_since),
  };
}

export function decodePersonQuestionnaire(body: unknown): BoardCoiPersonQuestionnaireOut | null {
  return decodeAsked<BoardCoiPersonQuestionnaireOut>(body);
}

export function decodeOwnQuestionnaire(body: unknown): BoardCoiQuestionnaireForMeOut | null {
  const read = decodeAsked<BoardCoiQuestionnaireForMeOut>(body);
  if (read === null || !isObject(read.intent_statement)) return null;
  return read;
}

export function extractOwnCycles(body: unknown): BoardCoiDirectorCycleOut[] {
  return extractList<BoardCoiDirectorCycleOut>(body, 'data').filter(
    (row) => isObject(row) && typeof row.cycle_id === 'string',
  );
}

// ── The register's annual answers ─────────────────────────────────────────────────────────────────

/** An annual response's block on a register row, or null for a single disclosure (§10.1). */
export function annualOf(entry: { annual?: unknown }): BoardConflictAnnualOut | null {
  if (!isObject(entry.annual) || typeof entry.annual.cycle_id !== 'string') return null;
  const annual = entry.annual as unknown as BoardConflictAnnualOut;
  return { ...annual, interests: arrayOf(annual.interests) };
}

/**
 * An annual interest's description beside its name, in the person's own words (o8-register): whose
 * as "Whose: Mine" and a position as "Position: director", the labels `entryRest` gives them too,
 * and what, as they wrote it.
 */
export function interestDescription(interest: {
  whose?: string | null;
  what?: string | null;
  position?: string | null;
  person?: string | null;
  relationship?: string | null;
}): string {
  const parts = [
    labelled('whose', text(interest.whose)),
    text(interest.what),
    labelled('position', text(interest.position)),
    text(interest.relationship),
  ].filter((value): value is string => value !== null);
  return parts.join(' · ');
}
